import { FlaskConical, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { DeviceStatic, Presence, Severity } from '../realtime/types';
import './modal.css';

export function Card({
  title,
  actions,
  children,
  bodyClass = 'card-body',
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  bodyClass?: string;
}) {
  return (
    <section className="card">
      {title !== undefined && (
        <header className="card-header">
          <span className="ellipsis">{title}</span>
          <span style={{ flex: 1 }} />
          {actions}
        </header>
      )}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <h1>{title}</h1>
      {subtitle && <span className="muted">{subtitle}</span>}
      <span style={{ flex: 1 }} />
      {actions}
    </div>
  );
}

export function PresenceBadge({ presence }: { presence: Presence | undefined }) {
  const { t } = useTranslation();
  const p = presence ?? 'unknown';
  return (
    <span className={`status status-${p}`}>
      <span className="dot" aria-hidden />
      {t(`presence.${p}`)}
    </span>
  );
}

/**
 * Phone-style signal indicator: five rising bars, `bars` of them lit (ui/signal.ts). One lit bar is drawn red;
 * 0 = "no signal". `stale` greys it out (the value is the last one reported while the device was connected).
 */
export function SignalBars({
  bars,
  title,
  stale = false,
}: {
  bars: number;
  title?: string;
  stale?: boolean;
}) {
  const tone = bars <= 1 ? 'is-weak' : '';
  return (
    <span
      className={`signal-bars ${tone}${stale ? ' is-stale' : ''}`}
      role="img"
      aria-label={title}
      title={title}
      data-bars={bars}
    >
      <svg viewBox="0 0 22 14" width="22" height="14" aria-hidden>
        {[0, 1, 2, 3, 4].map((i) => {
          const h = 4 + i * 2.5;
          return (
            <rect
              key={i}
              x={i * 4.5}
              y={14 - h}
              width="3"
              height={h}
              rx="0.8"
              className={i < bars ? 'on' : 'off'}
            />
          );
        })}
      </svg>
    </span>
  );
}

export function SeverityBadge({ severity }: { severity: Severity }) {
  const { t } = useTranslation();
  return <span className={`badge badge-${severity}`}>{t(`severity.${severity}`)}</span>;
}

export function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: ReactNode;
  tone?: 'online' | 'offline' | 'critical';
}) {
  return (
    <div className="card kpi">
      <span className="kpi-label">{label}</span>
      <span className="kpi-value" style={tone ? { color: `var(--color-${tone})` } : undefined}>
        {value}
      </span>
    </div>
  );
}

/** Marks screens that show generated demo data (mock data source). */
export function MockBadge() {
  const { t } = useTranslation();
  return (
    <span className="badge badge-mock" title={t('common.mockDataHint')}>
      <FlaskConical size={12} aria-hidden /> <span className="mock-label">{t('common.mockData')}</span>
    </span>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Right-side detail panel (UniFi-style). Non-modal on wide screens (the list stays usable),
 * full-width sheet on phones. Escape closes it.
 */
export function Drawer({
  title,
  subtitle,
  onClose,
  children,
  footer,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <aside className="drawer" role="dialog" aria-label={typeof title === 'string' ? title : undefined}>
      <header className="drawer-head">
        <div className="drawer-title">
          <strong className="ellipsis">{title}</strong>
          {subtitle && <span className="muted ellipsis">{subtitle}</span>}
        </div>
        <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
          <X size={18} />
        </button>
      </header>
      <div className="drawer-body">{children}</div>
      {footer && <footer className="drawer-foot">{footer}</footer>}
    </aside>
  );
}

/**
 * Large centred overlay (min(1200px, 94vw) × 90vh, scrollable body; full-screen sheet on phones) rendered in a portal
 * over a dark backdrop. Escape, the close button and a click on the backdrop close it. Focus moves into the dialog on
 * open and returns on close to `returnFocus` (the opening button) or, failing that, to the element that was focused
 * before; body scroll is locked while it is open. `label` is the dialog's accessible name (defaults to `title` when
 * that is a string). `size="small"`: a short form (≈ 440 px wide, as tall as its content), with an optional `footer`.
 */
let modalSeq = 0;
const openModals = new Set<number>();

export function Modal({
  title,
  subtitle,
  label,
  onClose,
  children,
  actions,
  returnFocus,
  size = 'large',
  footer,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  label?: string;
  onClose: () => void;
  children: ReactNode;
  /** controls shown in the header, left of the close button */
  actions?: ReactNode;
  /** element to focus again when the modal closes (normally the button that opened it) */
  returnFocus?: RefObject<HTMLElement | null>;
  size?: 'large' | 'small';
  /** buttons under the body */
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  const dialog = useRef<HTMLDivElement>(null);
  // opening order (render order: an outer modal before one opened from it), so Escape closes the top one only
  const [order] = useState(() => ++modalSeq);
  useEffect(() => {
    openModals.add(order);
    return () => void openModals.delete(order);
  }, [order]);
  useEffect(() => {
    const opener = returnFocus?.current ?? (document.activeElement as HTMLElement | null);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      if (opener && typeof opener.focus === 'function' && opener.isConnected) opener.focus();
    };
    // runs once per mount: the opener is captured when the modal opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || order !== Math.max(...openModals)) return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose, order]);
  return createPortal(
    // the backdrop is a click target for mouse users only; the close button and Escape cover keyboard users
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={dialog}
        className={size === 'small' ? 'modal modal-small' : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-label={label ?? (typeof title === 'string' ? title : undefined)}
        tabIndex={-1}
      >
        <header className="modal-head">
          <div className="modal-title">
            <strong className="ellipsis">{title}</strong>
            {subtitle && <span className="muted ellipsis">{subtitle}</span>}
          </div>
          {actions}
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
            <X size={18} />
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Labelled form row. Single inputs are wrapped in a <label>; groups of buttons (segmented controls) use
 * `group` so the label text does not become part of every button's accessible name.
 */
export function Field({
  label,
  hint,
  children,
  group = false,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
  group?: boolean;
}) {
  const content = (
    <>
      {label}
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </>
  );
  return group ? <div className="field">{content}</div> : <label className="field">{content}</label>;
}

/** Protocol the device speaks, as a compact tag labelled from the i18n catalog; unknown keys are shown as they are. */
export function ProtocolTag({ protocol }: { protocol: string | undefined | null }) {
  const { t } = useTranslation();
  if (!protocol) return null;
  return (
    <span className="protocol-tag">
      {t(
        `catalog.protocol.${protocol}` as 'common.unknown' /* any profile key; labels live in catalog.protocol */,
        { defaultValue: protocol },
      )}
    </span>
  );
}

/**
 * How a device communicates, derived (never stored): through its upstream device ("Radio via <model> <name>") or
 * directly with the hardware's link ("Direct LTE"). Model/link names are catalog data.
 */
export function connectivityLabel(
  t: (key: string, o?: Record<string, unknown>) => string,
  d: Pick<DeviceStatic, 'link' | 'upstream'>,
): string {
  if (d.upstream) {
    const name = [d.upstream.model, d.upstream.name].filter(Boolean).join(' ');
    return d.link === 'radio' ? t('connectivity.viaRadio', { name }) : t('connectivity.via', { name });
  }
  if (d.link)
    return t('connectivity.directLink', { link: t(`connectivity.link.${d.link}`, { defaultValue: d.link }) });
  return t('connectivity.direct');
}

export function ConnectivityTag({ device }: { device: Pick<DeviceStatic, 'link' | 'upstream'> }) {
  const { t } = useTranslation();
  return (
    <span className={device.upstream ? 'connectivity via' : 'connectivity'}>
      {connectivityLabel(t as (key: string, o?: Record<string, unknown>) => string, device)}
    </span>
  );
}
